import type { EvaluationSetting, FailWhen, MetricDistribution, SettingValue } from "./types";

/** Pure helpers of the evaluation settings page: value snapping, fire counts, histogram bars, changes. */

const EPS = 1e-9;

/** number of decimals of a step (0.0025 -> 4) */
export function stepDecimals(step: number | null | undefined): number {
  if (!step || !Number.isFinite(step)) return 2;
  const text = String(step);
  const exp = text.match(/e-(\d+)$/);
  if (exp) return Number(exp[1]);
  return text.split(".")[1]?.length ?? 0;
}

/** decimals of the percent shown for a ratio with this step (0.01 -> 0, 0.005 -> 1, 0.0025 -> 2) */
export const percentDigits = (step: number | null | undefined) => Math.max(0, stepDecimals(step) - 2);

export function sameValue(a: SettingValue | null | undefined, b: SettingValue | null | undefined): boolean {
  if (typeof a === "number" && typeof b === "number") return Math.abs(a - b) < EPS;
  return a === b;
}

/** clamp to [min, max] and round onto the step grid (from min), without float noise */
export function snapValue(value: number, setting: Pick<EvaluationSetting, "min" | "max" | "step">): number {
  const min = setting.min ?? 0;
  const max = setting.max ?? 1;
  const step = setting.step && setting.step > 0 ? setting.step : 0.01;
  const clamped = Math.min(max, Math.max(min, value));
  const snapped = min + Math.round((clamped - min) / step) * step;
  const decimals = Math.max(stepDecimals(step), stepDecimals(min));
  return Number(Math.min(max, snapped).toFixed(decimals));
}

/** pizzas the metric fires on at threshold `t` (exact on the bin grid, see the API contract) */
export function firesAt(dist: MetricDistribution, t: number): number {
  const k = Math.round((t - dist.lo) / dist.width);
  let fires = dist.over;
  for (let i = Math.max(0, k); i < dist.counts.length; i += 1) fires += dist.counts[i];
  if (dist.fail_when === "gte" && t <= dist.lo + EPS) fires += dist.under;
  return fires;
}

export interface HistogramBar {
  /** category key on the x axis */
  key: string;
  kind: "under" | "bin" | "over";
  /** bin index (bins only) */
  index: number;
  /** value range of the bar; null where it is open */
  start: number | null;
  end: number | null;
  count: number;
}

export const binKey = (index: number) => `b${index}`;

/** the bins, plus an "under" bar first and an "over" bar last when they hold values */
export function histogramBars(dist: MetricDistribution): HistogramBar[] {
  const bars: HistogramBar[] = [];
  if (dist.under > 0) {
    bars.push({ key: "under", kind: "under", index: -1, start: null, end: dist.lo, count: dist.under });
  }
  dist.counts.forEach((count, index) => {
    bars.push({
      key: binKey(index),
      kind: "bin",
      index,
      start: dist.lo + index * dist.width,
      end: dist.lo + (index + 1) * dist.width,
      count,
    });
  });
  if (dist.over > 0) {
    bars.push({ key: "over", kind: "over", index: dist.counts.length, start: dist.hi, end: null, count: dist.over });
  }
  return bars;
}

/** does the metric fire on the values of this bar at threshold `t` */
export function barFires(bar: HistogramBar, dist: MetricDistribution, t: number): boolean {
  if (bar.kind === "over") return true;
  if (bar.kind === "under") return dist.fail_when === "gte" && t <= dist.lo + EPS;
  return bar.index >= Math.round((t - dist.lo) / dist.width);
}

/**
 * Where a threshold sits on the categorical axis: the start edge of the first firing bin (the line
 * between the passing and the firing bars), or the end of the last bin when nothing past it is drawn.
 */
export function thresholdPosition(
  dist: MetricDistribution,
  bars: HistogramBar[],
  t: number,
): { x: string; position: "start" | "end" } | null {
  if (!bars.length) return null;
  const k = Math.round((t - dist.lo) / dist.width);
  if (k <= 0) {
    const first = bars.find((bar) => bar.kind === "bin");
    return first ? { x: first.key, position: "start" } : null;
  }
  if (k < dist.counts.length) return { x: binKey(k), position: "start" };
  if (dist.over > 0) return { x: "over", position: "start" };
  return { x: binKey(dist.counts.length - 1), position: "end" };
}

/** a step for the x ticks that gives at most `maxTicks` labels and lands on round values */
export function tickStep(dist: MetricDistribution, maxTicks = 6): number {
  const range = dist.hi - dist.lo;
  const candidates = [0.0025, 0.005, 0.01, 0.02, 0.025, 0.05, 0.1, 0.2, 0.25, 0.5, 1];
  return (
    candidates.find((step) => step >= dist.width - EPS && range / step <= maxTicks + EPS) ??
    Math.max(dist.width, range / maxTicks)
  );
}

/** keys of the bins whose start edge is a tick (plus the closing edge, drawn on the last bin) */
export function tickKeys(dist: MetricDistribution, maxTicks = 6): string[] {
  const step = tickStep(dist, maxTicks);
  const every = Math.max(1, Math.round(step / dist.width));
  const keys: string[] = [];
  for (let i = 0; i < dist.counts.length; i += every) keys.push(binKey(i));
  return keys;
}

/** the chart wants a log scale by default when one bar dwarfs the rest (zero-heavy metrics) */
export function prefersLogScale(dist: MetricDistribution): boolean {
  if (dist.n <= 0) return false;
  const largest = Math.max(dist.under, dist.over, ...dist.counts);
  return largest / dist.n > 0.25 || dist.zero / dist.n > 0.5;
}

/** y positions of 1, 10, 100, ... on a log10(count + 1) axis, up to the largest count */
export function logTicks(maxCount: number): number[] {
  const ticks: number[] = [];
  for (let c = 1; c <= Math.max(1, maxCount); c *= 10) ticks.push(toLog(c));
  return ticks;
}

/** top of a log10(count + 1) axis: a little above the largest bar */
export const logDomainMax = (maxCount: number) => toLog(Math.max(1, maxCount)) * 1.06;

export const toLog = (count: number) => Math.log10(count + 1);
export const fromLog = (y: number) => Math.round(10 ** y - 1);

export const opSymbol = (failWhen: FailWhen | null | undefined) => (failWhen === "gt" ? ">" : "≥");

// --- drafts and changes ---------------------------------------------------------------------------

export type Draft = Record<string, SettingValue>;

export interface PendingChange {
  setting: EvaluationSetting;
  old: SettingValue;
  next: SettingValue;
  /** the new value is the default: sent as null (follow the deployment default) */
  toDefault: boolean;
}

/** the edits that differ from the saved values, in display order */
export function pendingChanges(draft: Draft, settings: EvaluationSetting[]): PendingChange[] {
  const changes: PendingChange[] = [];
  for (const setting of settings) {
    if (!(setting.key in draft)) continue;
    const next = draft[setting.key];
    if (sameValue(next, setting.value)) continue;
    changes.push({ setting, old: setting.value, next, toDefault: sameValue(next, setting.default) });
  }
  return changes;
}

/** drop draft entries that equal the saved value (after a reload) or name a key that no longer exists */
export function pruneDraft(draft: Draft, settings: EvaluationSetting[]): Draft {
  const byKey = new Map(settings.map((s) => [s.key, s]));
  let changed = false;
  const next: Draft = {};
  for (const [key, value] of Object.entries(draft)) {
    const setting = byKey.get(key);
    if (!setting || sameValue(setting.value, value) || typeof setting.value !== typeof value) {
      changed = true;
      continue;
    }
    next[key] = value;
  }
  return changed ? next : draft;
}

/** PUT values: the default is sent as null so the key follows the deployment default again */
export function updateValues(changes: PendingChange[]): Record<string, SettingValue | null> {
  return Object.fromEntries(changes.map((c) => [c.setting.key, c.toDefault ? null : c.next]));
}
